import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

export default function VendorApply() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", location: "", description: "", gst: "", story: "", avatar_url: "" });
  const [existing, setExisting] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth"); return; }
    api.get("/vendors/me").then((r) => {
      if (r.data) { setExisting(r.data); setForm({ ...form, ...r.data }); }
    });
    // eslint-disable-next-line
  }, [user, loading]);

  const submit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      await api.post("/vendors/apply", form);
      await refresh();
      toast.success(existing ? "Details updated" : "Application submitted for review");
      const r = await api.get("/vendors/me");
      setExisting(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoadingSubmit(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="mb-8">
        <div className="label-caps mb-2">Sell with us</div>
        <h1 className="text-4xl md:text-5xl">Bring your craft to the world</h1>
        <p className="mt-3 text-[#4A5568]">Set up your artisan shop in minutes. Approval usually takes 24 hours.</p>
      </div>

      {existing && (
        <div className={`mb-6 p-4 rounded-xl border ${
          existing.status === "approved" ? "bg-[#EAF3EE] border-[#275F36] text-[#275F36]" :
          existing.status === "rejected" ? "bg-[#F8E4DE] border-[#B44618] text-[#B44618]" :
          "bg-[#FFF3EA] border-[#EAB89A] text-[#B44618]"
        }`}>
          <div className="font-bold uppercase text-xs tracking-wider">Status: {existing.status}</div>
          {existing.status === "approved" && (
            <button onClick={() => navigate("/vendor/dashboard")} data-testid="go-dashboard" className="mt-2 underline text-sm">Go to dashboard →</button>
          )}
        </div>
      )}

      <form onSubmit={submit} className="card-flat p-8 space-y-4">
        <div>
          <label className="label-caps">Business / Shop name</label>
          <input data-testid="vendor-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <div>
          <label className="label-caps">Location (City, State)</label>
          <input data-testid="vendor-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <div>
          <label className="label-caps">Short description</label>
          <textarea data-testid="vendor-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={2} className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <div>
          <label className="label-caps">Your story (Optional)</label>
          <textarea data-testid="vendor-story" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={4} placeholder="Tell buyers about your craft, family tradition, or journey…" className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <div>
          <label className="label-caps">GST number (Optional)</label>
          <input data-testid="vendor-gst" value={form.gst || ""} onChange={(e) => setForm({ ...form, gst: e.target.value })} className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <div>
          <label className="label-caps">Avatar image URL (Optional)</label>
          <input data-testid="vendor-avatar" value={form.avatar_url || ""} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" className="mt-2 w-full px-3 py-2.5 border border-[#E2DCD0] rounded-lg focus:outline-none focus:border-[#E25A24]" />
        </div>
        <button data-testid="vendor-submit" disabled={loadingSubmit} className="w-full btn-primary disabled:opacity-50">
          {loadingSubmit ? "Saving…" : (existing ? "Update details" : "Submit application")}
        </button>
      </form>
    </div>
  );
}
